import { Flex } from '@lideralia/alife-uikit/dist/atoms/Layout';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

/**
 * Component to display the alternative chat in a fullscreen iframe
 * with entrance and exit animation
 * 
 * Note: This component expects userData to contain userUuid, lineUuid and language parameters
 * which are passed to the iframe using postMessage. The userUuid and lineUuid parameters were previously
 * named user_id and line_id.
 * 
 * The component does not include a title bar as the iframe content already has its own.
 * The iframe can send a 'CHAT_CLOSED' event to close itself, which this component listens for.
 * Communication flow:
 * 1. Component sends 'CHAT_PARAMS' event with userData to iframe
 * 2. Iframe can send 'CHAT_READY' event to request parameters
 * 3. Iframe can send 'CHAT_CLOSED' event to close the chat
 * 4. Iframe can send 'CHAT_MINIMIZED' event to minimize the chat
 */
const AlternativeChat = ({
  visible,
  onClose,
  userData
}) => {
  const iframeUrl = process.env.REACT_APP_ALTERNATIVE_CHAT_URL || 'https://example.com';
  
  const [isAnimating, setIsAnimating] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  
  // Control visibility and animation when the visible prop changes
  useEffect(() => {
    if (visible) {
      // First make the component visible
      setIsVisible(true);
      // After a small delay, start the entrance animation
      setTimeout(() => {
        setIsAnimating(true);
      }, 50);
    } else {
      // First remove the animation
      setIsAnimating(false);
      // After the animation ends, hide the component
      setTimeout(() => {
        setIsVisible(false);
      }, 500); // This value should match the CSS transition duration
    }
  }, [visible]);

  // Send messages to the iframe using postMessage when relevant parameters change
  useEffect(() => {
    if (!isVisible) {
      return undefined;
    }
    
    const iframe = document.getElementById('alternative-chat-iframe');
    
    // Function to send data to the iframe
    const sendParamsToIframe = () => {
      if (iframe && iframe.contentWindow) {
        // Sending userData parameters to the iframe
        iframe.contentWindow.postMessage({
          type: 'CHAT_PARAMS',
          data: userData
        }, '*'); // The second parameter '*' allows sending messages to any origin
        console.log('Chat parameters sent to iframe with new parameter names (userUuid/lineUuid):', userData);
      }
      return undefined;
    };

    // Set up a listener to receive messages from the iframe
    const handleIframeMessages = (event) => {
      // Optionally, we could verify the origin for better security
      // if (event.origin !== URL.origin) return;
      
      if (event.data && event.data.type === 'CHAT_READY') {
        console.log('The iframe is ready to receive parameters');
        sendParamsToIframe();
      } else if (event.data && (event.data.type === 'CHAT_CLOSED' || event.data.type === 'CHAT_MINIMIZED')) {
        handleClose();
      }
      return undefined;
    };

    window.addEventListener('message', handleIframeMessages);

    // Send data when the iframe is loaded
    if (iframe) {
      // Send parameters when the iframe finishes loading
      iframe.onload = () => {
        sendParamsToIframe();
        return undefined;
      };
      
      // Also try to send now in case it's already loaded
      if (iframe.contentDocument && iframe.contentDocument.readyState === 'complete') {
        sendParamsToIframe();
      } else {
        // Give additional time just in case
        setTimeout(sendParamsToIframe, 1000);
      }
    }

    // Clean up the listener when the component unmounts
    return () => {
      window.removeEventListener('message', handleIframeMessages);
    };
  }, [isVisible, userData]);

  // Don't render anything if the component shouldn't be visible
  if (!isVisible) return null;

  // Function to handle closing with animation
  const handleClose = () => {
    // Start the closing animation and then call onClose
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 450); // Slightly less than the transition duration to make it look smooth
    return undefined;
  };

  return (
    <Flex 
      position="fixed"
      top={0}
      left={0}
      width="100%"
      height="100%"
      zIndex={10000}
      backgroundColor="rgba(0, 0, 0, 0.8)"
      justifyContent="center" 
      alignItems="flex-end"
      style={{
        opacity: isAnimating ? 1 : 0,
        transition: 'opacity 0.5s ease',
        overflow: 'hidden'
      }}
    >
      <Flex
        flexDirection="column"
        width="100%"
        maxHeight="100vh"
        backgroundColor="#fff"
        style={{
          height: isAnimating ? '100%' : '0%',
          transition: 'height 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          transformOrigin: 'bottom',
          boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
        }}
      >
        {/* iframe container - title bar removed as the iframe already has its own */}
        <Flex flex={1} position="relative">
          <iframe
            id="alternative-chat-iframe"
            src={iframeUrl}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 'none'
            }}
            title="Support Chat"
            allow="microphone; camera"
          />
        </Flex>
      </Flex>
    </Flex>
  );
};

AlternativeChat.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userData: PropTypes.shape({
    userUuid: PropTypes.string,
    lineUuid: PropTypes.string,
    language: PropTypes.string,
    userName: PropTypes.string,
    branchName: PropTypes.string,
    companyName: PropTypes.string
  })
};

AlternativeChat.defaultProps = {
  userData: {
    userUuid: '',
    lineUuid: '',
    language: 'en',
    userName: '',
    branchName: '',
    companyName: ''
  }
};

export default AlternativeChat;